/**
 * The TSR page's Not-in-Menu bookkeeping (spec §9.3). It is pure, so a
 * plain-node check can run it: backend/scripts/checks/check-tsr-page-lines.js.
 *
 * ONE NAME KEY. A line's key is the one the server's matcher sent (name_key),
 * else the same key computed here by the mirror of backend/services/posNameKey.js.
 * "Veg Pasta." and "veg pasta" are therefore one line on this page, as they are
 * one card in Not in Menu.
 *
 * REMEMBERED SKIPS. A line the server matched to a remembered Skip is set aside:
 * the owner already said "Packing Charges" is not a stock item. It is recorded
 * with the sale and never asked about. On submit the server decides the skip
 * again and never trusts the page's flag; the flag only keeps the line out of
 * this screen's questions.
 *
 * The ".js" on the import lets plain Node load this file; webpack resolves it
 * the same way either way.
 */
import { posNameKey } from './posNameKey.js';

// The portion words of spec §3 ("size siblings"), used to name a size option.
const PORTION_WORD = /\b(half|full|small|medium|large|regular|mini|jumbo|quarter|single|double|family)\b/i;
// How many names the set-aside note spells out before "…".
const NOTE_NAMES = 3;

const hasMenuId = (row) => Boolean(row?.apiData?.menuItemId || row?.menuItemId);
const nameOf = (row) => String(row?.item || row?.itemName || row?.item_name || row?.apiData?.item_name || '').trim();
const rupees = (n) => `₹${(Math.round((Number(n) || 0) * 100) / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

/** The name key of a scanned row or a Not-in-Menu entry: the server's, else the mirror's. */
export function lineNameKey(row) {
  if (row && typeof row.nameKey === 'string' && row.nameKey) return row.nameKey;
  return posNameKey(nameOf(row));
}

/**
 * A scanned line with no dish that the server matched to a remembered Skip.
 * A row on a dish is never set aside: the dish wins, whether it is an exact
 * match (D10) or the owner's own pick.
 */
export function isSetAsideRow(row) {
  if (!row || hasMenuId(row)) return false;
  return row.rememberedSkip === true || row.matchMethod === 'remembered_skip';
}

/**
 * Sum Not-in-Menu entries that share a name key, keeping the first spelling.
 * Duplicates are summed, never dropped: two bill lines of one unknown dish are
 * two real sales. A name with no letters or digits has an empty key, so it
 * merges only with its exact spelling, as every name did before. The inputs
 * are not modified.
 */
export function mergeByNameKey(entries) {
  const out = [];
  const byKey = new Map();
  for (const entry of entries || []) {
    if (!entry) continue;
    const nameKey = lineNameKey(entry);
    const mergeKey = nameKey || `\u0000${nameOf(entry)}`;
    const existing = byKey.get(mergeKey);
    if (existing) {
      existing.quantity += Number(entry.quantity) || 0;
      existing.amount += Number(entry.amount) || 0;
      if (!existing.priceUnresolved && entry.priceUnresolved) existing.priceUnresolved = entry.priceUnresolved;
    } else {
      const copy = { ...entry, nameKey, quantity: Number(entry.quantity) || 0, amount: Number(entry.amount) || 0 };
      byKey.set(mergeKey, copy);
      out.push(copy);
    }
  }
  return out;
}

/** "3 lines set aside as non-stock (Packing Charges, Swiggy, …)", or null when none. */
export function setAsideNote(rows) {
  const list = (rows || []).filter(Boolean);
  if (list.length === 0) return null;
  const names = [];
  const seen = new Set();
  for (const row of list) {
    const key = lineNameKey(row) || nameOf(row);
    if (seen.has(key)) continue;
    seen.add(key);
    names.push(nameOf(row) || 'Unnamed line');
  }
  const shown = names.slice(0, NOTE_NAMES).join(', ') + (names.length > NOTE_NAMES ? ', …' : '');
  return `${list.length} line${list.length === 1 ? '' : 's'} set aside as non-stock (${shown})`;
}

/**
 * "sold at ₹250, doesn't match Half ₹180 / Full ₹320": a size name whose price
 * matched no remembered size (Review Focus #2). Each option is named by its
 * size word, or in full when it has none. Returns null when there is nothing to say.
 */
export function priceUnresolvedNote(priceUnresolved) {
  const options = Array.isArray(priceUnresolved?.options) ? priceUnresolved.options : [];
  if (options.length === 0) return null;
  const sizeOf = (name) => {
    const m = String(name || '').match(PORTION_WORD);
    return m ? m[1].charAt(0).toUpperCase() + m[1].slice(1).toLowerCase() : String(name || '').trim();
  };
  const choices = options.map((o) => `${sizeOf(o.name)} ${rupees(o.price)}`).join(' / ');
  return `sold at ${rupees(priceUnresolved.line_price)}, doesn't match ${choices}`;
}

// ───────────────────────────────────────────────────────────────────────────
// Manual entry: "Add as Not in Menu" (spec 2026-10-26-tsr-add-as-not-in-menu)
// ───────────────────────────────────────────────────────────────────────────
// A dish missing from Item Mapping is marked on its row from the dish dropdown
// (apiData.notInMenu). Staff type its price per plate; amount = price × plates.

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const isExplicit = (row) => row?.apiData?.notInMenu === true;

/** The dropdown's last option, for what was typed: the trimmed name, or null when too short. */
export function notInMenuOptionFor(text) {
  const name = String(text ?? '').trim();
  return name.length >= 2 ? name : null;
}

/**
 * Manual rows the owner marked Not in Menu, and rows with a typed name but no
 * dish and no mark (they must be resolved before Confirm, never sent at ₹0).
 */
export function splitManualRows(manualItems) {
  const explicit = [];
  const unpicked = [];
  for (const row of manualItems || []) {
    if (!row || !nameOf(row)) continue;
    if (isExplicit(row)) explicit.push(row);
    else if (!hasMenuId(row)) unpicked.push(row);
  }
  return { explicit, unpicked };
}

const priceOf = (row) => Number(row?.apiData?.unitPrice ?? row?.apiData?.rate) || 0;
const platesOf = (row) => Number(row?.apiData?.quantity ?? row?.quantity) || 0;

/** A marked row as the Not-in-Menu entry /tsr/submit records. */
export function manualNotInMenuEntry(row) {
  const quantity = platesOf(row);
  const unitPrice = round2(priceOf(row));
  return {
    menuItemId: null,
    itemName: nameOf(row),
    nameKey: lineNameKey(row),
    reason: 'not_in_menu',
    quantity,
    unitPrice,
    amount: round2(unitPrice * quantity),
  };
}

/**
 * Why Confirm must stop: names of unpicked rows, of marked rows with no price, and of
 * marked rows with no plates (a 0-plate line could never be mapped, and would hold
 * up the closing count until skipped).
 */
export function manualRowProblems(manualItems) {
  const { explicit, unpicked } = splitManualRows(manualItems);
  return {
    unpicked: unpicked.map(nameOf),
    unpriced: explicit.filter((r) => !(priceOf(r) > 0)).map(nameOf),
    unquantified: explicit.filter((r) => !(platesOf(r) > 0)).map(nameOf),
  };
}
